import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Dialog, DialogSize, Icon, Spinner } from '@economic/taco';
import { Trans, t } from '@lingui/macro';
import { useCurrentLocale } from '@economic/react-app';
import { ProgressDots } from '~/app/shared/components/ui/progressDots/ProgressDots';
import useQuery from '~/app/shared/hooks/useQuery';
import internalApiClient from '~/app/shared/util/api/internalApiClient';
import type { CompanyResponse } from '~/payment-cards-feature';
import Introduction from './Introduction';
import { CreateGrantRequest, OnboardingStatus, OnboardingStep, RackbeatPlan, RackbeatPrinciple } from './types';
import IntegrationCompleted from './IntegrationCompleted';
import RackbeatLogo from './images/rackbeat-logo.svg';
import ConfirmTransferData from './ConfirmTransferData';
import TransferDataInProgress from './TransferDataInProgress';
import ConfirmInformation from './ConfirmInformation';
import InventoryPrinciple from './InventoryPrinciple';
import Subscription from './Subscription';
import ErrorDialog from './ErrorDialog';
import { trackEvent } from './trackEvent';
import AlreadyOnboarded from './AlreadyOnboarded';

function RackbeatLagerOnboardingDialog() {
    const defaultStepsCount = 6;
    const location = useLocation();
    const navigate = useNavigate();
    const open = location.hash === '#onboardRackbeatLagerNative';
    const { data: companyData } = useQuery<CompanyResponse>('/company/getCurrentCompany', null, {});
    const { data: onboardingStatus } = useQuery<OnboardingStatus>('/rackbeat-lager/company/onboarding-status', null, {});

    const locale = useCurrentLocale();
    const { currencyCode } = locale;

    const [isLoading, setIsLoading] = useState(false);
    const [subscriptionPlan, setSubscriptionPlan] = useState(RackbeatPlan.Premium);
    const [inventoryPrinciple, setInventoryPrinciple] = useState(RackbeatPrinciple.Average);
    const [consent, setConsent] = useState<boolean>(false);
    const [addExtraTab, setAddExtraTab] = useState(true);
    const [stepsCount, setStepsCount] = useState(defaultStepsCount);
    const [currentStep, setCurrentStep] = useState<OnboardingStep>(OnboardingStep.Introduction);
    const [errorOccurred, setErrorOccurred] = useState(false);
    const [startTime, setStartTime] = useState(0);
    const [rackbeatIdentificationToken, setRackbeatIdentificationToken] = useState<string>('');

    useEffect(() => {
        trackOnboardingEvent('step.loaded');
    }, [currentStep]);

    useEffect(() => {
        checkIfAlreadyOnboarded();
        setStartTime(performance.now());
    }, []);

    const checkIfAlreadyOnboarded = async () => {
        try {
            setIsLoading(true);
            const response = await internalApiClient.get('rackbeat-lager/company/is-already-onboarded');

            if (response.data !== undefined) {
                if (response.data) {
                    setCurrentStep(OnboardingStep.AlreadyOnboarded);
                } else {
                    setCurrentStep(OnboardingStep.Introduction);
                }
            } else {
                setErrorOccurred(true);
                trackOnboardingEvent('error-occurred');
            }
        } catch (error) {
            setErrorOccurred(true);
            trackOnboardingEvent('error-occurred');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOnOnboardingCompleted = async () => {
        trackOnboardingEvent('accept-and-transfer.clicked');

        setConsent(consent === true);
        if (!consent) {
            return;
        }

        try {
            setIsLoading(true);
            const request: CreateGrantRequest = {
                currencyCode,
                subscriptionPlan,
                inventoryPrinciple,
                rackbeatIdentificationToken,
                consentToNewsletter: consent,
            };
            const response = await internalApiClient.post('rackbeat-lager/company/initiate-onboarding', request);

            if (response.data) {
                setCurrentStep(OnboardingStep.TransferDataInProgress);
            } else {
                setErrorOccurred(true);
                trackOnboardingEvent('error-occurred');
            }
        } catch (error) {
            setErrorOccurred(true);
            trackOnboardingEvent('error-occurred');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOnAcceptTransfer = () => {
        trackOnboardingEvent('ok-proceed.clicked');

        setCurrentStep(OnboardingStep.IntegrationCompleted);
    };

    const handleOnSubscriptionPlan = (subscriptionPlan: RackbeatPlan) => {
        setSubscriptionPlan(subscriptionPlan);

        if (subscriptionPlan === RackbeatPlan.Basic) {
            setStepsCount(defaultStepsCount - 1);
        } else {
            setStepsCount(defaultStepsCount);
        }
    };

    const refreshPage = () => {
        trackOnboardingEvent('total-flow-duration', { duration: (performance.now() - startTime) / 1000 });

        navigate(location.pathname);
    };

    const handleCancelFlow = () => {
        trackOnboardingEvent('cancel.clicked');

        refreshPage();
    };

    const gotoNextStep = () => {
        trackOnboardingEvent('next.clicked');

        if (currentStep === OnboardingStep.Subscription) {
            setCurrentStep(subscriptionPlan === RackbeatPlan.Premium ? currentStep + 1 : currentStep + 2);
        } else {
            setCurrentStep(currentStep + 1);
        }
    };

    const gotoPreviousStep = () => {
        trackOnboardingEvent('previous.clicked');

        if (currentStep === OnboardingStep.ConfirmInformation) {
            setCurrentStep(subscriptionPlan === RackbeatPlan.Premium ? currentStep - 1 : currentStep - 2);
        } else {
            setCurrentStep(currentStep - 1);
        }
    };

    const getDialogSize = (): DialogSize => {
        if (currentStep === OnboardingStep.TransferDataInProgress) {
            return 'xs';
        }

        return 'xs';
    };

    const trackOnboardingEvent = (eventName: string, trackingData?: object) => {
        trackEvent(currentStep, eventName, trackingData);
    };

    if (isLoading) {
        return (
            <Dialog open={open} onClose={handleCancelFlow} size="xs">
                <Dialog.Content className="flex flex-col items-center" aria-label={t`TOAST_LOADING`}>
                    <Spinner />
                </Dialog.Content>
            </Dialog>
        );
    }
    if (errorOccurred) {
        return (
            <Dialog size="xs" onClose={handleCancelFlow} open>
                <Dialog.Content aria-label="rackbeatlager">
                    <ErrorDialog
                        message={t`A technical error occurred on our side, so we are unable to create your agreement at the moment.`}>
                        <></>
                    </ErrorDialog>
                    <Dialog.Footer className="flex justify-between bg-grey-100 -mx-6 -mb-6 align-middle pt-2 pb-4">
                        <div className="flex text-end ml-6 pt-2">
                            <div className="text-xs mr-4 mt-1">
                                <Trans>In close collaboration with</Trans>
                            </div>
                            <div>
                                <img src={RackbeatLogo} className="h-[24px]" />
                            </div>
                        </div>
                    </Dialog.Footer>
                </Dialog.Content>
            </Dialog>
        );
    }

    if (onboardingStatus === OnboardingStatus.Onboarded) {
        return (
            <Dialog size="xs" onClose={handleCancelFlow} open>
                <Dialog.Content aria-label="rackbeatlager">
                    <Dialog.Title>
                        <Trans>Oops! You are already onboarded!</Trans>
                    </Dialog.Title>
                    <div className="flex justify-end">
                        <div className="space-x-4">
                            <Button appearance="primary" onClick={handleCancelFlow}>
                                <Trans>Ok</Trans>
                            </Button>
                        </div>
                    </div>
                    <Dialog.Footer className="flex justify-between bg-grey-100 -mx-6 -mb-6 align-middle pt-2 pb-4">
                        <div className="flex text-end ml-6 pt-2">
                            <div className="text-xs mr-4 mt-1">
                                <Trans>In close collaboration with</Trans>
                            </div>
                            <div>
                                <img src={RackbeatLogo} className="h-[24px]" />
                            </div>
                        </div>
                    </Dialog.Footer>
                </Dialog.Content>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={handleCancelFlow}
            closeOnEscape={false}
            size={getDialogSize()}
            showCloseButton={currentStep !== OnboardingStep.TransferDataInProgress}>
            <Dialog.Content aria-label="rackbeatlager">
                {currentStep === OnboardingStep.Introduction && (
                    <Introduction>
                        <ProgressDots stepsCount={stepsCount} currentStep={currentStep} withCompleteTicks={false} />
                    </Introduction>
                )}
                {currentStep === OnboardingStep.Subscription && (
                    <Subscription
                        subscriptionPlan={subscriptionPlan}
                        setSubscriptionPlan={handleOnSubscriptionPlan}
                        trackEvent={trackOnboardingEvent}>
                        <ProgressDots stepsCount={stepsCount} currentStep={currentStep} withCompleteTicks={false} />
                    </Subscription>
                )}
                {currentStep === OnboardingStep.InventoryPrinciple && (
                    <InventoryPrinciple
                        inventoryPrinciple={inventoryPrinciple}
                        setInventoryPrinciple={setInventoryPrinciple}
                        trackEvent={trackOnboardingEvent}>
                        <ProgressDots stepsCount={stepsCount} currentStep={currentStep} withCompleteTicks={false} />
                    </InventoryPrinciple>
                )}
                {currentStep === OnboardingStep.ConfirmInformation && (
                    <ConfirmInformation companyData={companyData} currencyCode={currencyCode} trackEvent={trackOnboardingEvent}>
                        <ProgressDots stepsCount={stepsCount} currentStep={currentStep} withCompleteTicks={false} />
                    </ConfirmInformation>
                )}
                {currentStep === OnboardingStep.ConfirmTransferData && (
                    <ConfirmTransferData consent={consent} setConsent={setConsent}>
                        <ProgressDots stepsCount={stepsCount} currentStep={currentStep} withCompleteTicks={false} />
                    </ConfirmTransferData>
                )}
                {currentStep === OnboardingStep.TransferDataInProgress && (
                    <TransferDataInProgress handleOnAcceptTransfer={handleOnAcceptTransfer} trackEvent={trackOnboardingEvent}>
                        <></>
                    </TransferDataInProgress>
                )}
                {currentStep === OnboardingStep.IntegrationCompleted && (
                    <IntegrationCompleted addExtraTab={addExtraTab} setAddExtraTab={setAddExtraTab}>
                        <ProgressDots stepsCount={stepsCount} currentStep={currentStep} withCompleteTicks={false} />
                    </IntegrationCompleted>
                )}
                {currentStep === OnboardingStep.AlreadyOnboarded && (
                    <AlreadyOnboarded setAlreadyOnboardedId={setRackbeatIdentificationToken} />
                )}
                <div className="flex justify-end">
                    <div className="space-x-4">
                        {(currentStep === OnboardingStep.Introduction || currentStep === OnboardingStep.AlreadyOnboarded) && (
                            <Button appearance="default" onClick={handleCancelFlow}>
                                <Trans>Cancel</Trans>
                            </Button>
                        )}
                        {currentStep !== OnboardingStep.Introduction &&
                            currentStep !== OnboardingStep.TransferDataInProgress &&
                            currentStep !== OnboardingStep.IntegrationCompleted &&
                            currentStep !== OnboardingStep.AlreadyOnboarded && (
                                <Button appearance="default" onClick={gotoPreviousStep}>
                                    <Trans>Back</Trans>
                                </Button>
                            )}

                        {currentStep === OnboardingStep.ConfirmTransferData && (
                            <Button appearance="primary" onClick={handleOnOnboardingCompleted}>
                                <Trans>Accept and transfer</Trans>
                            </Button>
                        )}

                        {currentStep < OnboardingStep.TransferDataInProgress &&
                            currentStep !== OnboardingStep.ConfirmTransferData && (
                                <Button appearance="primary" onClick={gotoNextStep}>
                                    <Trans>Next</Trans>
                                </Button>
                            )}

                        {currentStep === OnboardingStep.IntegrationCompleted && (
                            <Button
                                appearance="primary"
                                href="https://app.rackbeat.com/login?locale=da"
                                onClick={() => {
                                    trackOnboardingEvent('total-flow-duration', {
                                        duration: (performance.now() - startTime) / 1000,
                                    });
                                    trackOnboardingEvent(`add-extra-tab.${addExtraTab ? 'selected' : 'not.selected'}`);
                                }}>
                                <div>
                                    <Trans>Go to Rackbeat</Trans>
                                </div>
                                <Icon name="undock" className="pl-5" />
                            </Button>
                        )}
                    </div>
                </div>
                {currentStep !== OnboardingStep.TransferDataInProgress && (
                    <Dialog.Footer className="flex justify-between bg-grey-100 -mx-6 -mb-6 align-middle pt-2 pb-4">
                        <div className="flex text-end ml-6 pt-2">
                            <div className="text-xs mr-4 mt-1">
                                <Trans>In close collaboration with</Trans>
                            </div>
                            <div>
                                <img src={RackbeatLogo} className="h-[24px]" />
                            </div>
                        </div>
                    </Dialog.Footer>
                )}
                {currentStep === OnboardingStep.TransferDataInProgress && (
                    <Dialog.Footer className="flex ustify-between bg-grey-100 p-6 -mx-6 -mb-6">
                        <div>
                            <Trans>
                                <strong>Did you know..</strong> that e-conomic and Rackbeat share office facilities and meet
                                almost daily to ensure that we offer the best inventory solution to our customers.
                            </Trans>
                        </div>
                    </Dialog.Footer>
                )}
            </Dialog.Content>
        </Dialog>
    );
}

export { RackbeatLagerOnboardingDialog as RackbeatLagerOnboardingDialogNative };
